import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Wallet, RefreshCw, Save, Calculator } from "lucide-react";

/** End-of-shift cash reconciliation: count the drawer vs Z-Report expected. */
export default function CashReconciliation() {
  const { currentCompany } = useAuth();
  const cur = currentCompany?.base_currency || "SYP";
  const [expected, setExpected] = useState(null);
  const [counted, setCounted] = useState("");
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  // Denomination breakdown — bills counts per denomination
  const DENOMS = cur === "SYP" ? [500, 1000, 2000, 5000, 10000, 50000] :
                 cur === "TRY" ? [5, 10, 20, 50, 100, 200] :
                 cur === "USD" ? [1, 5, 10, 20, 50, 100] :
                 [5, 10, 20, 50, 100, 200];
  const [bills, setBills] = useState({});
  const denomSum = DENOMS.reduce((s, d) => s + (bills[d] || 0) * d, 0);
  const setBill = (d, v) => { const n = { ...bills, [d]: Math.max(0, +v || 0) }; setBills(n); setCounted(String(DENOMS.reduce((s, dd) => s + (n[dd] || 0) * dd, 0))); };

  const loadExpected = async () => {
    try { const { data } = await api.get(`/cash-reconciliations/expected?date=${date}`); setExpected(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadHistory = async () => {
    try { const { data } = await api.get("/cash-reconciliations"); setHistory(data); }
    catch {}
  };
  useEffect(() => { loadExpected(); }, [date]);
  useEffect(() => { loadHistory(); }, []);

  const diff = expected && counted !== "" ? +counted - expected.expected_cash : null;

  const save = async () => {
    if (counted === "") return toast.error("أدخل النقدية المعدودة");
    setSaving(true);
    try {
      await api.post("/cash-reconciliations", { date, counted_cash: +counted, currency: cur, notes });
      toast.success("تم حفظ التسوية");
      setCounted(""); setNotes(""); loadHistory(); loadExpected();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="cash-reconciliation-page">
      <div className="flex items-start gap-3 flex-wrap justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2"><Wallet className="w-6 h-6 text-primary" /> تسوية الصندوق اليومية</h1>
          <p className="text-sm text-muted-foreground mt-1">عُدّ النقدية الفعلية في الدرج ثم قارنها بما هو متوقع من Z-Report</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">التاريخ</Label><Input type="date" value={date} onChange={(e)=>setDate(e.target.value)} className="h-9" data-testid="recon-date" /></div>
          <Button variant="outline" onClick={loadExpected} className="h-9 gap-1" data-testid="refresh-expected"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5 space-y-3">
          <div className="flex items-center gap-2 font-heading font-bold"><Calculator className="w-4 h-4" /> النقدية المتوقعة</div>
          {expected ? (
            <>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">مبيعات نقدية</span><span className="tabular-nums font-bold">{fmtMoney(expected.cash_sales, cur)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">المرتجعات</span><span className="tabular-nums font-bold text-red-600">- {fmtMoney(expected.returns_total, cur)}</span></div>
              <div className="border-t border-border pt-3 flex justify-between text-lg"><span className="font-heading font-bold">صافي المتوقع</span><span className="tabular-nums font-bold text-primary" data-testid="expected-amount">{fmtMoney(expected.expected_cash, cur)}</span></div>
              <div className="text-xs text-muted-foreground">{expected.sales_count} فاتورة</div>
            </>
          ) : <div className="text-sm text-muted-foreground">جاري التحميل...</div>}
        </Card>

        <Card className="p-5 space-y-3">
          <div className="flex items-center gap-2 font-heading font-bold"><Wallet className="w-4 h-4" /> النقدية الفعلية في الدرج</div>
          <div>
            <Label>عدّ النقدية</Label>
            <Input type="number" value={counted} onChange={(e)=>setCounted(e.target.value)} placeholder="مثلاً 150000" className="h-11 text-lg text-center font-bold tabular-nums" data-testid="counted-cash-input" />
          </div>
          <details className="border border-border rounded-md p-3 bg-muted/30" data-testid="denom-breakdown">
            <summary className="cursor-pointer text-sm font-bold flex items-center gap-2">💵 تفصيل الفئات النقدية <span className="text-xs text-muted-foreground font-normal">(اختياري — يُجمِع تلقائياً)</span></summary>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
              {DENOMS.map(d => (
                <div key={d} className="flex items-center gap-2">
                  <span className="text-xs w-16 shrink-0 tabular-nums font-bold text-muted-foreground">{d.toLocaleString("ar-EG")}</span>
                  <span className="text-xs">×</span>
                  <Input type="number" min={0} value={bills[d] || ""} onChange={(e)=>setBill(d, e.target.value)} placeholder="0" className="h-8 text-center" data-testid={`bill-${d}`} />
                </div>
              ))}
            </div>
            {denomSum > 0 && <div className="text-xs text-primary mt-2 text-end font-bold">مجموع الفئات: {denomSum.toLocaleString("ar-EG")} {cur}</div>}
          </details>
          <div>
            <Label>ملاحظات</Label>
            <Textarea rows={2} value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder="أي فرق أو ملاحظة" data-testid="recon-notes" />
          </div>
          {diff !== null && (
            <div className={`p-3 rounded-md text-center font-bold ${Math.abs(diff) < 1 ? "bg-emerald-100 text-emerald-800" : diff > 0 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`} data-testid="diff-badge">
              {Math.abs(diff) < 1 ? "✓ متطابق تماماً" : diff > 0 ? `+ ${fmtMoney(diff, cur)} فائض` : `${fmtMoney(diff, cur)} عجز`}
            </div>
          )}
          <Button onClick={save} disabled={saving} className="w-full gap-1 h-11" data-testid="save-recon-btn"><Save className="w-4 h-4" /> {saving ? "جاري..." : "حفظ التسوية"}</Button>
        </Card>
      </div>

      <Card className="p-2">
        <div className="text-sm font-bold p-3 border-b border-border">السجل</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>التاريخ</TableHead>
            <TableHead>الكاشير</TableHead>
            <TableHead>متوقع</TableHead>
            <TableHead>معدود</TableHead>
            <TableHead>الفرق</TableHead>
            <TableHead>ملاحظات</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {history.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">لا يوجد سجل</TableCell></TableRow>}
            {history.map(r => (
              <TableRow key={r.id} data-testid={`recon-${r.id}`}>
                <TableCell>{r.date}</TableCell>
                <TableCell className="font-bold">{r.cashier_name}</TableCell>
                <TableCell className="tabular-nums">{fmtMoney(r.expected_cash, r.currency)}</TableCell>
                <TableCell className="tabular-nums">{fmtMoney(r.counted_cash, r.currency)}</TableCell>
                <TableCell className={`tabular-nums font-bold ${Math.abs(r.difference) < 1 ? "text-emerald-600" : r.difference > 0 ? "text-amber-600" : "text-red-600"}`}>{r.difference > 0 ? "+" : ""}{fmtMoney(r.difference, r.currency)}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{r.notes || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
